/**
 * Generic Logger Utility with localStorage-based namespace control
 *
 * USAGE:
 * ------
 * import { createLogger } from '@utils/logger';
 * const log = createLogger('dateWithTz');
 * log.debug('message', { optional: 'data' });   // gated by DEBUG_NAMESPACES
 * log.error('message', error);                   // always logged
 *
 * ENABLE LOGGING IN BROWSER:
 * --------------------------
 * Open DevTools Console (F12) and run:
 *
 *   // Enable specific namespace
 *   localStorage.setItem('DEBUG_NAMESPACES', 'dateWithTz');
 *
 *   // Enable multiple namespaces
 *   localStorage.setItem('DEBUG_NAMESPACES', 'dateWithTz,expeditions,timeslots');
 *
 *   // Enable ALL namespaces
 *   localStorage.setItem('DEBUG_NAMESPACES', '*');
 *
 * DISABLE LOGGING:
 * ----------------
 *   localStorage.removeItem('DEBUG_NAMESPACES');
 *
 * NOTE: Changes take effect immediately for new logs (no page reload needed).
 *
 * DISCOVER LOGGERS FROM THE CONSOLE:
 * ----------------------------------
 * A `ringilLoggers` helper is exposed on `window`. It only knows about loggers whose
 * module has already been imported, so visit the relevant page first.
 *
 *   ringilLoggers.list()                          // list every registered namespace + enabled state
 *   ringilLoggers.enable('dateWithTz', 'timeslots') // add namespaces to DEBUG_NAMESPACES
 *   ringilLoggers.enableAll()                      // DEBUG_NAMESPACES = '*'
 *   ringilLoggers.disable('dateWithTz')            // remove namespaces (no args = clear all)
 *   ringilLoggers.disableAll()                     // clear DEBUG_NAMESPACES (counterpart to enableAll)
 */

const registeredNamespaces = new Set<string>();

const getEnabledNamespaces = (): string => localStorage.getItem('DEBUG_NAMESPACES') ?? '';

const isNamespaceEnabled = (namespace: string): boolean => {
  const enabled = getEnabledNamespaces();
  if (!enabled) return false;
  return enabled === '*' || enabled.split(',').some(ns => ns.trim() === namespace);
};

class Logger {
  private namespace: string;

  constructor(namespace: string) {
    this.namespace = namespace;
  }

  debug(message: string, data?: any): void {
    if (isNamespaceEnabled(this.namespace)) {
      console.log(`[${this.namespace}] ${message}`, data || '');
    }
  }

  /** Always logged, regardless of DEBUG_NAMESPACES — for genuine error conditions. */
  error(message: string, data?: any): void {
    console.error(`[${this.namespace}] ${message}`, data ?? '');
  }
}

export const createLogger = (namespace: string): Logger => {
  registeredNamespaces.add(namespace);
  return new Logger(namespace);
};

/** Console helper for discovering and toggling loggers; exposed on `window.ringilLoggers`. */
const ringilLoggers = {
  /** List every registered namespace (loggers whose module has been imported) and its enabled state. */
  list(): string[] {
    const namespaces = [...registeredNamespaces].sort();
    console.log(`Registered loggers (DEBUG_NAMESPACES=${JSON.stringify(getEnabledNamespaces())}):`);
    namespaces.forEach(ns => console.log(`  ${isNamespaceEnabled(ns) ? '✓' : '✗'} ${ns}`));
    return namespaces;
  },
  /** Add one or more namespaces to DEBUG_NAMESPACES (replaces a previous '*' with an explicit list). */
  enable(...namespaces: string[]): string[] {
    const current = getEnabledNamespaces();
    const base = current === '*' ? [] : current.split(',').map(ns => ns.trim()).filter(Boolean);
    localStorage.setItem('DEBUG_NAMESPACES', Array.from(new Set([...base, ...namespaces])).join(','));
    return this.list();
  },
  /** Enable every namespace (DEBUG_NAMESPACES = '*'). */
  enableAll(): string[] {
    localStorage.setItem('DEBUG_NAMESPACES', '*');
    return this.list();
  },
  /**
   * Remove one or more namespaces from DEBUG_NAMESPACES. With no arguments (or when everything is
   * enabled via '*'), clears DEBUG_NAMESPACES entirely — same as disableAll().
   */
  disable(...namespaces: string[]): string[] {
    const current = getEnabledNamespaces();
    if (namespaces.length === 0 || current === '*') {
      localStorage.removeItem('DEBUG_NAMESPACES');
      return this.list();
    }
    const remaining = current.split(',').map(ns => ns.trim()).filter(Boolean).filter(ns => !namespaces.includes(ns));
    if (remaining.length === 0) {
      localStorage.removeItem('DEBUG_NAMESPACES');
    } else {
      localStorage.setItem('DEBUG_NAMESPACES', remaining.join(','));
    }
    return this.list();
  },
  /** Disable every namespace by clearing DEBUG_NAMESPACES (the counterpart to enableAll()). */
  disableAll(): string[] {
    localStorage.removeItem('DEBUG_NAMESPACES');
    return this.list();
  },
};

if (typeof window !== 'undefined') {
  (window as any).ringilLoggers = ringilLoggers;
}

